import { useCallback, useMemo } from 'react';

import { useI18n } from '@change-corgi/core/react/i18n';
import { useTracking } from '@change-corgi/core/react/tracking';

import {
	useStarterDashboardV2BannerState,
	useStarterDashboardV2PageData,
	useStarterDashboardV2PageFcm,
} from 'src/app/pages/starterDashboardV2/pageContext';
import { isLoading } from 'src/app/shared/utils/async';

import { useBannerDismissalStorage } from '../../../../../PageBanner/hooks/useBannerDismissalStorage';
import { NOTIFICATION_TYPES } from '../../../../constants';
import { useCreateFreePromotion } from '../useCreateFreePromotion';

import { createErrorBanner, createSuccessBanner } from './helpers';
import type { UseFreePromotionNotificationReturn } from './types';

type Props = {
	onDismiss?: () => void;
};

export function useFreePromotionNotification({ onDismiss }: Props = {}): UseFreePromotionNotificationReturn {
	const { localizeCurrency } = useI18n();
	const {
		data: { petition, viewer },
		actions: { refreshData },
	} = useStarterDashboardV2PageData();
	const { pacificPromoFreeSample } = useStarterDashboardV2PageFcm();
	const { actions } = useStarterDashboardV2BannerState();
	const { setBannerDismissed } = useBannerDismissalStorage();
	const { translate, translateHtml } = useI18n();

	const track = useTracking();

	const { mutate, reset, ...createFreePromotionState } = useCreateFreePromotion({
		onSuccess: () => {
			refreshData();
			void track('free_promo_success');
			createSuccessBanner({ petitionId: petition.id, translate, setBannerDismissed, addBanner: actions.addBanner });
		},
		onError: () => {
			void track('free_promo_error');
			createErrorBanner({ petitionId: petition.id, translateHtml, setBannerDismissed, addBanner: actions.addBanner });
		},
	});
	const handleCreateFreePromotion = async (): Promise<void> => {
		void track('petition_manage_notification_cta_click', { notification: NOTIFICATION_TYPES.promotion });
		await mutate({ id: petition.id });
	};

	const handleDismiss = useCallback(() => {
		onDismiss?.();
	}, [onDismiss]);

	const amount = pacificPromoFreeSample?.amount || 0;
	const impressions = pacificPromoFreeSample?.impressions || 0;
	const viewersCurrencyCode = viewer.user?.country?.currency?.code;

	const amountFormatted = useMemo(() => {
		if (!viewersCurrencyCode) {
			return localizeCurrency(amount, 'USD');
		}

		return localizeCurrency(amount, viewersCurrencyCode);
	}, [localizeCurrency, amount, viewersCurrencyCode]);

	return {
		data: { loading: isLoading(createFreePromotionState), impressions, amountFormatted },
		actions: { handleCreateFreePromotion, handleDismiss },
	};
}
